import { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/Modal";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";
import { Button } from "../../../components/Button";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import { userRoleLabels, userRoles } from "../../../constant";
import { Field } from "../../certifications/components/CertificationFormModal";
import { toast } from "react-hot-toast";
import { ShowGenericErrorToast } from "../../../services/toast";

export function UserFormModal({
  isOpen,
  onClose,
  user,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    role: userRoles.AccountSpecialist,
    territoryId: "",
    regionId: "",
    productId: "",
    managerId: "",
    trainerId: "",
    phone_number: "",
    zoneId: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const { mutate, isLoading: isLoadingMutation } = useMutation({
    mutationFn: (data: any) => api.createUser(data),
  });

  const { mutate: updateUser, isLoading: isLoadingUpdate } = useMutation({
    mutationFn: (data: any) => api.updateUserAttributes(data.id, data),
  });

  const { data: territoriesData, isLoading: isLoadingTerritories } = useQuery({
    queryKey: ["territories"],
    queryFn: () => api.getTerritories(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: regionsData, isLoading: isLoadingRegions } = useQuery({
    queryKey: ["regions"],
    queryFn: () => api.getRegions(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: zonesData } = useQuery({
    queryKey: ["zones"],
    queryFn: () => api.getZones(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const editUser = !!user;

  const handleSave = () => {
    if (isDisabled) return;
    setIsLoading(true);
    const payload = {
      id: user?.id,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      role: form.role,
      managerId: form.managerId,
      // trainerId: form.trainerId,
      // regionIds: [form.regionId],
      // productIds: [form.productId],
      // territoryIds: [form.territoryId],
      zoneId: form.zoneId,
    };
    const callbacks = {
      onSuccess: () => {
        setIsLoading(false);
        handleClose();
        onSuccess();
        toast.success(
          editUser
            ? "User updated"
            : "User added and received the invitation email"
        );
      },
      onError: () => {
        setIsLoading(false);
        ShowGenericErrorToast();
      },
    };
    if (user?.id) {
      updateUser(payload, callbacks);
    } else {
      mutate(payload, callbacks);
    }
  };

  const handleClose = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      managerId: "",
      trainerId: "",
      regionId: "",
      productId: "",
      territoryId: "",
      phone_number: "",
      zoneId: "",
    });
    onClose();
  }

  const territories = territoriesData?.data || [];
  const regions = regionsData?.data || [];
  const zones = zonesData?.data || [];
  const users = usersData || [];

  const isSaving = isLoading || isLoadingMutation;

  const isDisabled = useMemo(() => {
    return !form.firstName || !form.lastName || !form.email || !form.role;
  }, [form]);

  // When modal opens: if editing a user, repopulate form; if adding (no user), reset to empty.
  // Depends on isOpen so that reopening for the same user still repopulates after handleClose cleared the form.
  useEffect(() => {
    if (!isOpen) return;
    if (user) {
      setForm((prev) => ({
        ...prev,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        managerId: user?.manager?.id,
        trainerId: user?.trainer?.id,
        regionId: user?.regions?.[0]?.id,
        productId: user?.products?.[0]?.id,
        territoryId: user?.territories?.[0]?.id,
        zoneId: user?.zoneId ?? user?.zone?.id ?? user?.zones?.[0]?.id ?? "",
      }));
    } else {
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        role: userRoles.AccountSpecialist,
        territoryId: "",
        regionId: "",
        productId: "",
        managerId: "",
        trainerId: "",
        phone_number: "",
        zoneId: "",
      });
    }
  }, [user, isOpen]);

  return (
    <Modal
      contentClassName="!px-0"
      title="Edit Profile"
      isOpen={isOpen}
      handleModal={handleClose}
    >
      <div className="max-h-[calc(100vh-125px)] overflow-auto">
        <div className="flex flex-col gap-4 px-5">
          <div className="flex gap-4 items-start">
            <Field label="First Name" className="flex-1">
              <input
                type="text"
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                value={form.firstName}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }));
                }}
              />
            </Field>
            <Field label="Last Name" className="flex-1">
              <input
                type="text"
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                value={form.lastName}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }));
                }}
              />
            </Field>
          </div>
          <div className="flex gap-4 items-center w-full">
            <Field label="Email" className="flex-1">
              <input
                type="email"
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                value={form.email}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }));
                }}
              />
            </Field>
            <Field label="Role" className="flex-1">
              <SelectInput
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    role: e.target.value,
                  }));
                }}
                value={form.role || ""}
                options={[
                  { label: "Select Role", value: "" },
                  ...Object.keys(userRoles)
                  .filter((option) => userRoles[option] !== userRoles.SalesRep)
                  .map((option) => ({
                    label: userRoleLabels[option],
                    value: userRoles[option],
                  })),
                ]}
                placeholder="Select Role"
              />
            </Field>
          </div>
          <div className="flex gap-4 items-center">
            <Field label="Region" className="flex-1">
              <SelectInput
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    regionId: e.target.value,
                  }));
                }}
                value={form.regionId || ""}
                options={[
                  { label: "Select Region", value: "" },
                  ...regions.map((option) => ({
                    label: option.name,
                    value: option.id,
                  })),
                ]}
                placeholder="Select Region"
              />
            </Field>
            <div className="flex gap-4 items-center flex-1">
              <Field label="Territory" className="flex-1">
                <SelectInput
                  value={form.territoryId || ""}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      territoryId: e.target.value,
                    }));
                  }}
                  options={territories?.map((territory) => ({
                    label: territory.name,
                    value: territory.id,
                  }))}
                  placeholder="Select Location"
                />
              </Field>
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex gap-4 items-center flex-1">
              <Field label="Manager" className="flex-1">
                <SelectInput
                  value={form.managerId || ""}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      managerId: e.target.value,
                    }));
                  }}
                  options={users?.map((user) => ({
                    label: `${user.firstName} ${user.lastName} (${user.email})`,
                    value: user.id,
                  }))}
                  placeholder="Select Manager"
                />
              </Field>
            </div>
            <div className="flex gap-4 items-center flex-1">
              <Field label="Trainer" className="flex-1">
                <SelectInput
                  value={form.trainerId}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      trainerId: e.target.value,
                    }));
                  }}
                  options={users?.map((user) => ({
                    label: user.firstName,
                    value: user.id,
                  }))}
                  placeholder="Select Trainer"
                />
              </Field>
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <Field label="Zone" className="flex-1">
              <SelectInput
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    zoneId: e.target.value,
                  }));
                }}
                value={form.zoneId || ""}
                options={[
                  ...zones.map((option) => ({
                    label: option.name,
                    value: option.id,
                  })),
                ]}
                placeholder="Select Zone"
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              variant="primary"
              className="h-[58px] !rounded-full "
              loading={isSaving}
              disabled={isDisabled}
            >
              {editUser ? "Update User" : "Create User"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
